import { useSyncExternalStore, useState, useEffect } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

const TRACKED_NETWORK_EVENTS: (keyof WindowEventMap)[] = ['load', 'online', 'offline'];

const isOnlineSubscribe: Parameters<typeof useSyncExternalStore<boolean>>[0] = (callback) => {
	TRACKED_NETWORK_EVENTS.forEach((event) => window.addEventListener(event, callback));
	return () => {
		TRACKED_NETWORK_EVENTS.forEach((event) => window.removeEventListener(event, callback));
	};
};

const isOnlineGetSnapshot: Parameters<typeof useSyncExternalStore<boolean>>[1] = () =>
	navigator.onLine;

// During SSR rendering, we can't check the navigator status, so instead we'll assume the user is online
const isOnlineGetServerSnapshot: Parameters<typeof useSyncExternalStore<boolean>>[2] = () => true;

const pingUrl = 'https://status.atlassian.com/api/v2/status.json'; // Status endpoint from: https://status.atlassian.com/api#status
const pingTimeoutMs = 3000;
let confirmationPingPromise: Promise<boolean> | null = null; // Cache the confirmation ping, avoids several fetch's.

export const useEditorNetworkStatus = () => {
	const browserIsOnline = useSyncExternalStore(
		isOnlineSubscribe,
		isOnlineGetSnapshot,
		isOnlineGetServerSnapshot,
	);

	const [effectiveIsOnline, setEffectiveIsOnline] = useState(browserIsOnline);

	// navigator.onLine is not reliable, and can be affected by proxies and other network issues.
	// Therefore we use our browserIsOnline value with a confirmation ping on init to determine if the user is genuinely offline.
	// We only call this on init AND navigator.onLine is false, rather than every time the hook is called, to avoid spamming the status endpoint.
	// Note: The confirmation ping is the source of truth for initial state validation; we always apply it to override potential browser flakiness.
	// Warning: an invalid navigator.onLine value can be used if it updates after init. If so, the confirmation ping may be needed on every browserIsOnline change.
	useEffect(() => {
		// If the browser is online, we can immediately set the effectiveIsOnline state to true. We're trying to catch incorrect offline cases.
		if (browserIsOnline) {
			setEffectiveIsOnline(true);
		} else {
			if (confirmationPingPromise === null) {
				confirmationPingPromise = new Promise((resolve) => {
					const controller = new AbortController();
					const timeoutId = setTimeout(() => controller.abort(), pingTimeoutMs);

					fetch(pingUrl, { signal: controller.signal, credentials: 'omit' })
						.then(() => resolve(true))
						.catch(() => resolve(false))
						.finally(() => {
							clearTimeout(timeoutId);
							confirmationPingPromise = null;
						});
				});
			}

			void confirmationPingPromise.then((confirmedOnline) => {
				setEffectiveIsOnline(confirmedOnline);
			});
		}
		// Intentionally not including browserIsOnline in the dependency array, as we only want to run this once on init.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setEffectiveIsOnline(browserIsOnline);
	}, [browserIsOnline]);

	const isNetworkDisabled = expVal('platform_editor_offline_editing_web', 'isEnabled', false)
		? !effectiveIsOnline && !fg('platform_editor_offline_editing_wrapper_override')
		: false;

	return {
		isNetworkDisabled,
	};
};
